<template>
  <div
    id="app"
    class="container w-4/5 sm:w-3/5"
  >
    <div
      class="flex sm:justify-center items-center w-full absolute 
      font-bold text-xl top-0 left-0 rounded-t-lg p-2"
    >
      <h1 class="text-2xl font-light tracking-wider">Tasky</h1>

      <!-- Mark all Tasks button  -->
      <button
        title="Mark as done all tasks"
        class="flex absolute right-0 mr-14 sm:mr-20 focus:outline-none"
        @click="markAllDone()"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8 btn hover:scale-110 hover:bg-yellow-700 active:bg-yellow-500 rounded-full p-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      <!-- Delete All tasks button -->
      <button
        title="Delete all tasks"
        class="flex absolute right-0 mr-6 sm:mr-10 focus:outline-none"
        @click="deleteAll()"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 btn hover:text-red-500 hover:scale-110"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2
            2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"
          />
        </svg>
      </button>
    </div>

    <!-- Input Task -->
    <form @submit.prevent="addNewTask()" class="">
      <label
        class="flex absolute bottom-0 left-0 w-full bg-yellow-600 rounded-b-lg"
      >
        <input
          v-model="newTask"
          type="text"
          placeholder="What we have for today?"
          class="bg-transparent w-full p-3 pl-8 placeholder-gray-200 focus:outline-none italic font-light"
        />

        <!-- Add Task button -->
        <button
          type="submit"
          class="mr-6 btn rounded-full px-2 focus:outline-none active:bg-yellow-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 5l7 7-7 7M5 5l7 7-7 7"
            />
          </svg>
        </button>
      </label>
    </form>

    <!-- Tasks -->
    <div
      class="tasksContainer space-y-4 overflow-y-auto max-h-96"
      :class="{ 'last:pb-12': tasks.length > 0 }"
    >
    
      <!-- No data found -->
      <div v-if="tasks.length == 0" class="text-center">
        <img src="./assets/nodata.svg" class="w-64 mx-auto" />
        <p class="mt-6 font-semibold text-4xl">Oops!</p>
        <h2 class="mt-1 text-xl font-light mb-10">Any task Found. Add one now!</h2>
      </div>

      <!-- Layout Tasks -->
      <div
        id="task"
        v-for="task in tasks"
        :key="task.id"
        @click="taskDone(task)"
        class="tasksContent"
        :class="{'bg-gray-900 line-through': task.isDone}"
      >
        <h1>{{ task.text }}</h1>

        <!-- Delete button -->
        <span
          class="p-1 cursor-pointer rounded-full transition text-red-500"
          :class="[
            task.isDone
              ? 'text-blue-50'
              : 'hover:bg-red-500 hover:bg-opacity-25',
          ]"
          @click="deleteTask(task)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>


<script>
import { ref, onMounted } from "vue";

export default {
  name: "App",
  setup() {
    // Add a new task
    const newTask = ref("");

    // All the Tasks stored here
    const tasks = ref([]);

    onMounted(() => {
      if (localStorage.getItem("saveTask")) {
        let saveTask = JSON.parse(localStorage.getItem("saveTask"));
        saveTask.forEach((task) => {
          tasks.value.push(task);
        });
      }
    });

    // Function to add new Task
    function addNewTask() {
      // Check if the input has text
      if (newTask.value.trim()) {
        tasks.value.push({
          id: Date.now(),
          text: newTask.value,
          isDone: false,
        });
        // Turn the input clean after insert any task
        newTask.value = "";

        // this is for keep the data in the HTML5 browser, even when closed.
        localStorage.setItem("saveTask", JSON.stringify(tasks.value));
      }
    }

    // When clicked in the task, it marks as done or undone
    function taskDone(task) {
      task.isDone = !task.isDone;

      // this is for keep the data in the HTML5 browser, even when closed.
      localStorage.setItem("saveTask", JSON.stringify(tasks.value));
    }

    // Function to delete the task
    function deleteTask(task) {
      tasks.value = tasks.value.filter((item) => {
        return item != task;
      });
      localStorage.setItem("saveTask", JSON.stringify(tasks.value));
    }

    // Function to mark all tasks as done
    function markAllDone() {
      tasks.value.forEach((task) => {
        task.isDone = true;
      });

      // this is for keep the data in the HTML5 browser, even when closed.
      localStorage.setItem("saveTask", JSON.stringify(tasks.value));
    }
    
    // Function to delete all the tasks
    function deleteAll() {
      tasks.value.forEach((task) => {
        tasks.value = tasks.value.filter((item) => {
          return item != task;
        });
      });

      // this is for keep the data in the HTML5 browser, even when closed.
      localStorage.setItem("saveTask", JSON.stringify(tasks.value));
    }

    return {
      newTask,
      tasks,
      addNewTask,
      taskDone,
      deleteTask,
      markAllDone,
      deleteAll,
    };
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700&display=swap");
#app {
  font-family: "Montserrat", sans-serif;
}
</style>
